import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Avatar,
  Box,
  Tooltip,
  Typography,
  useTheme,
  Icons,
} from '@ublend-npm/aulaui-core';
import { STUDENT_ROLE } from '@core/constants/space';
import { HomeViewRole } from '@app/constants/home';
import { HomeUpcomingAssignment } from '@core/assignments/api/homeUpcomingAssignments';
import { getAssignmentProvider } from '@core/assignments/utils';
import { getLtiProviders } from '@core/selectors/lti';
import useIsClamped from '@app/hooks/useIsClamped';
import { getAssignmentProviderIconSrc } from './getAssignmentProviderIcon';

const STRINGS = {
  dueDate: 'Due Date',
  noDueDate: 'No due date',
  submitted: 'Submitted',
};

const STATUS_LABELS: Record<HomeUpcomingAssignment['status'], string> = {
  Upcoming: 'Upcoming',
  DueSoon: 'Due soon',
  Open: 'Open',
};

const getAssignmentInitial = (title?: string) => {
  const trimmed = title?.trim();
  if (!trimmed) {
    return '?';
  }

  return trimmed.charAt(0).toUpperCase();
};

const formatAssignmentDueDate = (dueDate: string | null) => {
  if (!dueDate) {
    return STRINGS.noDueDate;
  }

  const date = new Date(dueDate);

  if (Number.isNaN(date.getTime())) {
    return STRINGS.noDueDate;
  }

  return date.toLocaleDateString('en-GB');
};

type TruncatedTextProps = {
  text: string;
  isTruncated: boolean;
  setRef: (node: HTMLDivElement | null) => void;
  sx?: Record<string, unknown>;
  sxText?: Record<string, unknown>;
};

const TruncatedText = ({
  text,
  isTruncated,
  setRef,
  sx,
  sxText,
}: TruncatedTextProps) => {
  const content = (
    <Box
      ref={setRef}
      tabIndex={0}
      aria-label={text}
      sx={{
        minWidth: 0,
        overflow: 'hidden',
        ...sx,
      }}
    >
      <Typography sx={{ ...sxText }}>{text}</Typography>
    </Box>
  );

  if (!isTruncated) {
    return content;
  }

  return (
    <Tooltip title={text} placement="top" arrow>
      {content}
    </Tooltip>
  );
};

type UpcomingAssignmentRowProps = {
  assignment: HomeUpcomingAssignment;
  viewRole: HomeViewRole;
  termLabel?: string | null;
  showDivider?: boolean;
  onOpen?: (assignment: HomeUpcomingAssignment) => void | Promise<void>;
};

const UpcomingAssignmentRow = ({
  assignment,
  viewRole,
  termLabel = null,
  showDivider = true,
  onOpen,
}: UpcomingAssignmentRowProps) => {
  const { palette } = useTheme();
  const formattedDueDate = formatAssignmentDueDate(assignment.dueDate);
  const [titleRef, isTitleClamped] = useIsClamped<HTMLDivElement>(
    assignment.title
  );
  const [courseNameRef, isCourseNameClamped] = useIsClamped<HTMLDivElement>(
    assignment.courseName
  );
  const canOpenAssignment =
    !assignment.cta.disabled &&
    Boolean(onOpen) &&
    (Boolean(assignment.cta.path) ||
      Boolean(assignment.provider) ||
      assignment.role === 'educator');

  const providers = useSelector(getLtiProviders);
  const assignmentInitial = getAssignmentInitial(assignment.title);
  const ltiProvider = getAssignmentProvider(
    {
      provider: assignment.provider,
      providerId: assignment.providerId,
    } as any,
    providers
  );
  const providerIconSrc = getAssignmentProviderIconSrc({
    provider: assignment.provider,
    imageSrc: ltiProvider?.iconUrl,
  });
  const [hasImageError, setHasImageError] = useState(false);

  useEffect(() => {
    setHasImageError(false);
  }, [assignment.id, providerIconSrc]);

  const showProviderImage = Boolean(providerIconSrc) && !hasImageError;

  const handleRowClick = useCallback(() => {
    if (!canOpenAssignment || !onOpen) {
      return;
    }

    onOpen(assignment);
  }, [assignment, canOpenAssignment, onOpen]);

  const handleRowKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleRowClick();
      }
    },
    [handleRowClick]
  );

  const shouldShowSubmittedIndicator =
    viewRole === STUDENT_ROLE && assignment.isSubmitted;

  const statusIndicator =
    shouldShowSubmittedIndicator ? null : assignment.isHidden &&
      viewRole !== STUDENT_ROLE ? (
      <Tooltip title="Hidden assignment" placement="top" arrow>
        <Icons.VisibilityOffOutlined
          tabIndex={0}
          sx={{
            fontSize: 20,
            color: palette.text.disabled,
            flexShrink: 0,
          }}
          aria-label="Hidden assignment"
        />
      </Tooltip>
    ) : (
      <Tooltip title={STATUS_LABELS[assignment.status]} placement="top" arrow>
        <Box
          role="status"
          aria-label={STATUS_LABELS[assignment.status]}
          tabIndex={0}
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor:
              assignment.status === 'DueSoon'
                ? palette.warning.light
                : palette.info.light,
            flexShrink: 0,
          }}
        />
      </Tooltip>
    );

  const assignmentAvatar = showProviderImage ? (
    <Box
      aria-hidden="true"
      sx={{
        width: 40,
        height: 40,
        flexShrink: 0,
        borderRadius: '50%',
        overflow: 'hidden',
        backgroundColor: 'transparent',
      }}
    >
      <img
        src={providerIconSrc}
        alt=""
        onError={() => setHasImageError(true)}
        style={{
          width: 40,
          height: 40,
          objectFit: 'cover',
          display: 'block',
        }}
      />
    </Box>
  ) : (
    <Avatar
      fallbackText={assignmentInitial}
      altText={assignment.title}
      sx={{
        width: 40,
        height: 40,
        flexShrink: 0,
        cursor: 'inherit',
        fontSize: '1rem',
        fontWeight: 500,
      }}
      aria-hidden="true"
    />
  );

  return (
    <Box
      display="flex"
      alignItems="flex-start"
      gap={2}
      sx={{
        borderBottom: showDivider ? `1px solid ${palette.divider}` : 'none',
        mb: showDivider ? '8px' : 0,
      }}
    >
      <Box
        display="flex"
        alignItems="flex-start"
        gap={2}
        role={canOpenAssignment ? 'button' : undefined}
        tabIndex={canOpenAssignment ? 0 : undefined}
        onClick={canOpenAssignment ? handleRowClick : undefined}
        onKeyDown={canOpenAssignment ? handleRowKeyDown : undefined}
        aria-label={
          canOpenAssignment
            ? assignment.cta.label || assignment.title
            : undefined
        }
        sx={{
          minWidth: 0,
          width: '100%',
          p: '8px',
          mb: '8px',
          cursor: canOpenAssignment ? 'pointer' : 'default',
          '&:hover': {
            bgcolor: 'action.hover',
            borderRadius: '8px',
            width: '100%',
          },
        }}
      >
        {assignmentAvatar}

        <Box flex={1} minWidth={0} display="flex" gap={1}>
          <Box flex={1} minWidth={0}>
            <TruncatedText
              text={assignment.title}
              isTruncated={isTitleClamped}
              setRef={titleRef}
              sx={{
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 2,
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                color: 'text.primary',
                fontSize: '16px',
                fontWeight: 400,
                lineHeight: 1.25,
                letterSpacing: '0.17px',
              }}
              sxText={{
                variant: 'body2',
                fontWeight: 550,
                fontSize: '16px',
                lineHeight: '150%',
                letterSpacing: '0.17px',
              }}
            />

            <Typography
              variant="caption"
              color="text.secondary"
              component="p"
              sx={{
                lineHeight: '166%',
                fontSize: '14px',
                m: 0,
                display: 'block',
              }}
              tabIndex={0}
              aria-label={STRINGS.dueDate + formattedDueDate}
            >
              {STRINGS.dueDate}: {formattedDueDate}
            </Typography>

            {assignment.courseName ? (
              <TruncatedText
                text={assignment.courseName}
                isTruncated={isCourseNameClamped}
                setRef={courseNameRef}
                sx={{
                  mt: '8px',
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: 1,
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  color: 'text.secondary',
                  fontSize: '14px',
                  fontWeight: 400,
                  lineHeight: 1.43,
                  letterSpacing: '0.17px',
                }}
                sxText={{
                  variant: 'body2',
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '150%',
                  letterSpacing: '0.17px',
                }}
              />
            ) : null}

            {termLabel ? (
              <Typography
                variant="body2"
                color="text.secondary"
                component="p"
                sx={{
                  lineHeight: 1.43,
                  m: 0,
                  display: 'flex',
                  alignItems: 'center',
                }}
                tabIndex={0}
                aria-label={termLabel}
              >
                {termLabel.split(' | ').map((part, index) => (
                  <React.Fragment key={`${part}-${index}`}>
                    {index > 0 ? (
                      <Box component="span" aria-hidden sx={{ px: '8px' }}>
                        |
                      </Box>
                    ) : null}
                    <Box component="span">{part}</Box>
                  </React.Fragment>
                ))}
              </Typography>
            ) : null}
          </Box>

          <Box
            display="flex"
            alignItems="flex-start"
            gap={0.75}
            flexShrink={0}
            sx={{ pt: '8px', pr: '8px' }}
          >
            {shouldShowSubmittedIndicator && (
              <Tooltip title={STRINGS.submitted} placement="top" arrow>
                <Icons.CheckCircle
                  role="status"
                  aria-label={STRINGS.submitted}
                  sx={{
                    fontSize: 20,
                    color: palette.success.main,
                    flexShrink: 0,
                  }}
                />
              </Tooltip>
            )}
            {statusIndicator}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default UpcomingAssignmentRow;
